import { Cancel, Check, Warning } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { getNodesAvailable } from 'lib/api/decisionEngine';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import { DecisionNode } from 'lib/types/models';
import {
  Node,
  Range,
  RevisionObjective,
} from 'pages/Settings/Components/Risk/Components/DecisionEngines/components/Create/types';
import React from 'react';
import { useDispatch } from 'react-redux';

const color = (node: DecisionNode) => {
  switch (node.type) {
    case 'final':
      switch (node.name) {
        case 'Aprobado':
          return <Check style={{ color: BASE_COLORS.green }} />;
        case 'Rechazado':
          return <Cancel style={{ color: BASE_COLORS.red }} />;
        default:
          return <Warning style={{ color: BASE_COLORS.yellow }} />;
      }
    default:
      return <Check style={{ color: BASE_COLORS.blue }} />;
  }
};

function getPreviousRange(
  decisionNodesSortedBySeverity: DecisionNode[],
  node: Node,
  index: number,
) {
  for (let i = index - 1; i >= 0; i--) {
    if (decisionNodesSortedBySeverity[i]) {
      const decisionNode = decisionNodesSortedBySeverity[i];
      const previousRange = node.ranges.find(
        (range) => range.nodeId === decisionNode.id,
      );
      if (previousRange) return previousRange;
    }
  }
  return null;
}

const NodeComponent = ({
  node,
  decisionNodesSortedBySeverity,
}: {
  node: Node;
  decisionNodesSortedBySeverity: DecisionNode[];
}) => {
  const getEqRangeString = (ranges: Range[]) => {
    if (ranges.length === 0) {
      return null;
    }
    if (ranges.length === 1) {
      return {
        value: ranges[0].value,
        nodeId: ranges[0].nodeId,
      };
    }
    const eqValue = ranges.map((range) => range.value).join(', ');
    return {
      value: eqValue,
      nodeId: ranges[0].nodeId,
    };
  };

  const formattByType = (value: string | number | undefined) => {
    switch (node.type) {
      case 'boolean':
        return value ? 'Si' : 'No';
      case 'percentage':
        return `${value}%`;
      case 'amount':
        return value;
      case 'money':
        if (typeof value !== 'number') return `${value}`;
        if (value < 1000000) return `${formatMoney(value)}`;
        return `${formatMoney(value / 1000000)}M`;
      default:
        return value;
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: 52, margin: '10px 0px' }}>
      {decisionNodesSortedBySeverity.map((decisionNode, index) => {
        const ranges = node.ranges.filter(
          (range) => range.nodeId === decisionNode.id,
        );
        const range =
          node.operation === 'eq' ? getEqRangeString(ranges) : ranges[0];
        const previousRange = getPreviousRange(
          decisionNodesSortedBySeverity,
          node,
          index,
        );
        return (
          <div
            key={index}
            style={{
              padding: '0px 0px 0px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 200,
              textAlign: 'center',
            }}
          >
            {range && (
              <div>
                {node.type === 'boolean' && (
                  <Typography variant="h6" gutterBottom>
                    {range.value === '1' ? 'Si' : 'No'}
                  </Typography>
                )}
                {node.type !== 'boolean' && (
                  <Typography variant="h6" gutterBottom>
                    {node.operation === 'gt' && (
                      <Typography variant="h6" gutterBottom>
                        {`${
                          previousRange
                            ? formattByType(previousRange.value)
                            : `${formattByType('0')}`
                        }  a ${
                          range.value !== '' ? formattByType(range.value) : '∞'
                        }`}
                      </Typography>
                    )}
                    {node.operation === 'eq' && (
                      <LightTooltip
                        title={
                          <Typography
                            variant="body1"
                            style={{
                              padding: '10px',
                              cursor: 'pointer',
                            }}
                          >
                            {range.value}
                          </Typography>
                        }
                        placement="top"
                      >
                        <Typography variant="h6" gutterBottom>
                          {range.value === ''
                            ? 'Ninguno'
                            : range?.value && range?.value?.length > 10
                              ? 'Varios'
                              : range.value}
                        </Typography>
                      </LightTooltip>
                    )}
                    {node.operation === 'lt' && (
                      <Typography variant="h6" gutterBottom>
                        {`${
                          range.value === '' ? '0' : formattByType(range.value)
                        }`}{' '}
                        a{' '}
                        {previousRange
                          ? formattByType(previousRange.value)
                          : `∞`}
                      </Typography>
                    )}
                  </Typography>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MatrixVisualization: React.FC<{
  nodes: Node[];
  revisionObjective: RevisionObjective;
}> = ({ nodes, revisionObjective }) => {
  const dispatch = useDispatch();
  const { data } = getNodesAvailable(dispatch, revisionObjective);
  const decisionNodes = data?.decisionNodes || [];

  const decisionResultsInNodes = nodes
    .map((node) => {
      return node.ranges
        .map((range) => {
          return range.nodeId;
        })
        .flat();
    })
    .flat();

  const decisionNodesToDisplay = decisionNodes.filter((decisionNode) =>
    decisionResultsInNodes.includes(decisionNode.id),
  );
  const decisionNodesSortedBySeverity = decisionNodesToDisplay.sort((a, b) => {
    return (a.severity ?? 0) - (b.severity ?? 0);
  });

  return (
    <div
      style={{
        width: '100%',
        overflowX: 'auto',
        marginLeft: '8px',
      }}
    >
      <div style={{ display: 'flex', padding: '8px 0px' }}>
        {decisionNodesSortedBySeverity.map((node, index) => {
          return (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 200,
                textAlign: 'center',
                justifyContent: 'center',
              }}
            >
              {color(node)}
              <LightTooltip
                title={<Typography variant="body1">{node.name}</Typography>}
                placement="top"
              >
                <Typography marginLeft="8px" variant="h6" gutterBottom>
                  {node.name}
                </Typography>
              </LightTooltip>
            </div>
          );
        })}
      </div>
      <div style={{ padding: '20px 0px' }}>
        {nodes.map((node, index) => {
          return (
            <NodeComponent
              key={index}
              node={node}
              decisionNodesSortedBySeverity={decisionNodesSortedBySeverity}
            />
          );
        })}
      </div>
    </div>
  );
};

export default MatrixVisualization;
